import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { Eye, BarChart3, PieChart as PieChartIcon, TrendingUp, Download, Sparkles, ChevronRight, ChevronLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import html2canvas from 'html2canvas';
import { toast } from '@/hooks/use-toast';
import { useChartAnalysis } from '@/hooks/useChartAnalysis';
import { AIInsights } from './AIInsights';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface CustomDataTableWidgetProps {
  config: {
    customData?: {
      name: string;
      columns: Array<{ id: string; name: string; type: 'text' | 'number' | 'date' }>;
      rows: Array<Record<string, any>>;
    };
  };
  onEdit?: () => void;
  onInsightsToggle?: (isOpen: boolean) => void;
}

const COLORS = [
  'hsl(220 91% 42%)',  // Bleu professionnel
  'hsl(142 76% 36%)',  // Vert
  'hsl(35 90% 55%)',   // Orange
  'hsl(0 75% 55%)',    // Rouge
  'hsl(271 91% 65%)',  // Violet
  'hsl(45 93% 47%)',   // Jaune
  'hsl(340 82% 52%)',  // Rose
  'hsl(200 98% 39%)',  // Cyan
  'hsl(20 90% 48%)',   // Orange brûlé
  'hsl(280 87% 47%)',  // Magenta
  'hsl(160 60% 45%)',  // Vert-bleu
  'hsl(310 65% 50%)'   // Fuchsia
];

export const CustomDataTableWidget: React.FC<CustomDataTableWidgetProps> = ({ config, onEdit, onInsightsToggle }) => {
  const [viewMode, setViewMode] = useState<'table' | 'bar' | 'pie' | 'line'>('bar');
  const [selectedXColumn, setSelectedXColumn] = useState<string>('');
  const [selectedYColumn, setSelectedYColumn] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSlice, setSelectedSlice] = useState<string | null>(null);
  const [selectedSliceIndex, setSelectedSliceIndex] = useState<number | null>(null);
  const [maxItems, setMaxItems] = useState<number>(10);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isAIPanelOpen, setIsAIPanelOpen] = useState<boolean>(false);
  const itemsPerPage = 20;
  const {
    isAnalyzing,
    insights,
    analyzeChart,
    clearInsights,
    quota,
    analysisAge,
    isCached,
  } = useChartAnalysis(
    viewMode !== 'table' ? viewMode : undefined,
    config.customData?.name
  );

  // Charger les préférences sauvegardées
  React.useEffect(() => {
    if (config.customData) {
      const savedPreferences = localStorage.getItem(`chart-prefs-${config.customData.name}`);
      if (savedPreferences) {
        const prefs = JSON.parse(savedPreferences);
        setSelectedXColumn(prefs.xColumn || '');
        setSelectedYColumn(prefs.yColumn || '');
        setViewMode(prefs.viewMode || 'bar');
        setMaxItems(prefs.maxItems || 10);
        setSortOrder(prefs.sortOrder || 'desc');
        setIsAIPanelOpen(prefs.isAIPanelOpen !== undefined ? prefs.isAIPanelOpen : false);
      }
    }
  }, [config.customData?.name]);

  // Sauvegarder les préférences quand elles changent
  const updateXColumn = (value: string) => {
    setSelectedXColumn(value);
    savePreference('xColumn', value);
  };

  const updateYColumn = (value: string) => {
    setSelectedYColumn(value);
    savePreference('yColumn', value);
  };

  const updateViewMode = (mode: 'table' | 'bar' | 'pie' | 'line') => {
    setViewMode(mode);
    savePreference('viewMode', mode);
  };

  const updateMaxItems = (value: number) => {
    setMaxItems(value);
    savePreference('maxItems', value);
  };

  const updateSortOrder = (value: 'asc' | 'desc') => {
    setSortOrder(value);
    savePreference('sortOrder', value);
  };

  const toggleAIPanel = () => {
    const newState = !isAIPanelOpen;
    setIsAIPanelOpen(newState);
    savePreference('isAIPanelOpen', newState);
    onInsightsToggle?.(newState);
  };

  const savePreference = (key: string, value: any) => {
    if (config.customData) {
      const savedPrefs = localStorage.getItem(`chart-prefs-${config.customData.name}`) || '{}';
      const prefs = JSON.parse(savedPrefs);
      prefs[key] = value;
      localStorage.setItem(`chart-prefs-${config.customData.name}`, JSON.stringify(prefs));
    }
  };

  if (!config.customData) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Aucune donnée configurée
      </div>
    );
  }

  const { name, columns, rows } = config.customData;
  const numberColumns = columns.filter(col => col.type === 'number');
  const textColumns = columns.filter(col => col.type === 'text');
  
  // Pagination
  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const paginatedRows = rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Préparer les données pour les graphiques
  // Fonction pour tronquer le texte
  const truncateText = (text: string, maxLength: number = 15) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const prepareChartData = () => {
    if (!selectedXColumn || !selectedYColumn) return [];
    
    let data = rows.map((row, index) => ({
      name: truncateText(String(row[selectedXColumn] || '')), // Nom tronqué pour l'affichage
      fullName: row[selectedXColumn], // Nom complet pour le tooltip
      value: parseFloat(row[selectedYColumn]) || 0, // Valeur pour l'axe Y
      color: COLORS[index % COLORS.length], // Couleur pour chaque barre
      [selectedXColumn]: row[selectedXColumn], // Conserver la donnée originale
      [selectedYColumn]: parseFloat(row[selectedYColumn]) || 0 // Conserver la donnée originale
    }));

    // Trier les données
    data.sort((a, b) => {
      if (sortOrder === 'desc') {
        return b.value - a.value;
      }
      return a.value - b.value;
    });

    // Limiter le nombre d'éléments et réassigner les couleurs après le tri
    const limitedData = data.slice(0, maxItems);
    return limitedData.map((item, index) => ({
      ...item,
      color: COLORS[index % COLORS.length]
    }));
  };

  const chartData = prepareChartData();

  // Handle legend click
  const handleLegendClick = (data: any) => {
    if (selectedSlice === data.value) {
      setSelectedSlice(null);
      setSelectedSliceIndex(null);
    } else {
      const index = chartData.findIndex(item => item.name === data.value);
      setSelectedSlice(data.value);
      setSelectedSliceIndex(index);
    }
  };

  // Handle pie slice click
  const handlePieClick = (data: any, index: number) => {
    if (selectedSlice === data.name) {
      setSelectedSlice(null);
      setSelectedSliceIndex(null);
    } else {
      setSelectedSlice(data.name);
      setSelectedSliceIndex(index);
    }
  };

  // Export chart as image
  const exportChart = async () => {
    const chartElement = document.querySelector(`[data-chart-name="${name}"]`);
    if (chartElement) {
      try {
        const canvas = await html2canvas(chartElement as HTMLElement, {
          backgroundColor: '#ffffff',
          scale: 2, // Higher quality
          useCORS: true,
        });
        
        // Convert to blob and download
        canvas.toBlob((blob) => {
          if (blob) {
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.download = `graphique-${name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            toast({
              title: "Export réussi",
              description: "Le graphique a été exporté en PNG."
            });
          }
        }, 'image/png');
      } catch (error) {
        toast({
          title: "Erreur d'export",
          description: "Impossible d'exporter le graphique.",
          variant: "destructive"
        });
      }
    }
  };

  // Analyze chart with AI
  const handleAnalyzeChart = () => {
    const chartTypeMap = {
      bar: 'Graphique en barres',
      pie: 'Graphique circulaire',
      line: 'Graphique linéaire',
      table: 'Tableau de données',
    };
    
    // Toujours utiliser les colonnes sélectionnées par l'utilisateur et les données complètes
    const xColumnName = columns.find(col => col.id === selectedXColumn)?.name || selectedXColumn;
    const yColumnName = columns.find(col => col.id === selectedYColumn)?.name || selectedYColumn;
    
    const dataToAnalyze = rows.slice(0, 100).map(row => ({
      name: String(row[selectedXColumn || columns[0]?.id] || ''),
      value: parseFloat(row[selectedYColumn || columns.find(c => c.type === 'number')?.id] || 0) || 0,
      [xColumnName]: row[selectedXColumn || columns[0]?.id],
      [yColumnName]: row[selectedYColumn || columns.find(c => c.type === 'number')?.id],
    }));
    
    analyzeChart(
      dataToAnalyze,
      `${chartTypeMap[viewMode] || 'Données'} - Colonne X: ${xColumnName}, Colonne Y: ${yColumnName}`,
      name,
      true // Force new analysis
    );
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const xColumnName = columns.find(col => col.id === selectedXColumn)?.name || selectedXColumn;
      const yColumnName = columns.find(col => col.id === selectedYColumn)?.name || selectedYColumn;
      
      // Récupérer la valeur complète de la colonne X depuis le payload
      const xValue = payload[0].payload.fullName || payload[0].payload[selectedXColumn] || payload[0].name || label;
      
      return (
        <div className="bg-background border rounded-lg p-2 shadow-lg">
          <p className="font-medium">{`${xColumnName}: ${xValue}`}</p>
          <p className="text-primary">{`${yColumnName}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (!selectedXColumn || !selectedYColumn || chartData.length === 0) {
      return (
        <div className="p-4 text-center text-muted-foreground">
          <p>Sélectionnez les colonnes pour afficher le graphique</p>
          <div className="mt-4 space-y-2 max-w-xs mx-auto">
            <Select value={selectedXColumn} onValueChange={updateXColumn}>
              <SelectTrigger>
                <SelectValue placeholder="Colonne X (catégories)" />
              </SelectTrigger>
              <SelectContent>
                {textColumns.map(col => (
                  <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYColumn} onValueChange={updateYColumn}>
              <SelectTrigger>
                <SelectValue placeholder="Colonne Y (valeurs)" />
              </SelectTrigger>
              <SelectContent>
                {numberColumns.map(col => (
                  <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    const xColumnName = columns.find(col => col.id === selectedXColumn)?.name || selectedXColumn;
    const yColumnName = columns.find(col => col.id === selectedYColumn)?.name || selectedYColumn;

    switch (viewMode) {
      case 'bar':
        return (
          <div 
            data-chart-name={name} 
            role="img" 
            aria-label={`Graphique en barres : ${name}`}
            aria-describedby={`chart-desc-${name.replace(/\s+/g, '-')}`}
          >
            <div id={`chart-desc-${name.replace(/\s+/g, '-')}`} className="sr-only">
              {generateAccessibleDescription()}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          </div>
        );

      case 'pie':
        return (
          <div 
            data-chart-name={name}
            role="img" 
            aria-label={`Graphique circulaire : ${name}`}
            aria-describedby={`chart-desc-${name.replace(/\s+/g, '-')}`}
          >
            <div id={`chart-desc-${name.replace(/\s+/g, '-')}`} className="sr-only">
              {generateAccessibleDescription()}
            </div>
            <ResponsiveContainer width="100%" height={500}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  outerRadius={140}
                  fill="hsl(var(--primary))"
                  label={(entry) => `${entry.value}`}
                  onClick={(data, index) => handlePieClick(data, index)}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      stroke={selectedSlice === entry.name ? '#000' : 'none'}
                      strokeWidth={selectedSlice === entry.name ? 2 : 0}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom" 
                  height={60}
                  wrapperStyle={{ 
                    paddingTop: '10px',
                    fontSize: '12px'
                  }}
                  onClick={handleLegendClick}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );

      case 'line':
        return (
          <div 
            data-chart-name={name}
            role="img" 
            aria-label={`Graphique linéaire : ${name}`}
            aria-describedby={`chart-desc-${name.replace(/\s+/g, '-')}`}
          >
            <div id={`chart-desc-${name.replace(/\s+/g, '-')}`} className="sr-only">
              {generateAccessibleDescription()}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
          </div>
        );

      default:
        return null;
    }
  };

  // Générer une description textuelle pour les lecteurs d'écran
  const generateAccessibleDescription = () => {
    if (viewMode === 'table') {
      return `Tableau ${name} contenant ${rows.length} lignes de données avec les colonnes suivantes : ${columns.map(c => c.name).join(', ')}. Page ${currentPage} sur ${totalPages}.`;
    }
    
    if (!chartData.length || !selectedXColumn || !selectedYColumn) {
      return `Graphique ${name} - Aucune donnée à afficher`;
    }
    
    const xColumnName = columns.find(col => col.id === selectedXColumn)?.name || selectedXColumn;
    const yColumnName = columns.find(col => col.id === selectedYColumn)?.name || selectedYColumn;
    const chartTypeLabel = viewMode === 'bar' ? 'en barres' : viewMode === 'pie' ? 'circulaire' : 'linéaire';
    
    const dataDescription = chartData.map((item, index) => 
      `${index + 1}. ${item.fullName || item.name} : ${item.value}`
    ).join(', ');
    
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    const avg = (total / chartData.length).toFixed(2);
    
    return `Graphique ${chartTypeLabel} intitulé ${name}. Axe horizontal : ${xColumnName}. Axe vertical : ${yColumnName}. 
      Affichage de ${chartData.length} éléments triés par ordre ${sortOrder === 'desc' ? 'décroissant' : 'croissant'}. 
      Total : ${total}. Moyenne : ${avg}. 
      Détails des données : ${dataDescription}`;
  };

  return (
    <div className="space-y-4" role="region" aria-label={`Widget de données : ${name}`}>
      {/* Description accessible cachée visuellement */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {generateAccessibleDescription()}
      </div>
      
      {/* Contrôles de vue */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{name}</h3>
        <div className="flex items-center gap-2">
          {onEdit && (
            <Button
              onClick={onEdit}
              size="sm"
              variant="ghost"
              className="text-xs"
            >
              Modifier
            </Button>
          )}
          <Button
            onClick={() => updateViewMode('table')}
            size="sm"
            variant={viewMode === 'table' ? 'default' : 'outline'}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => updateViewMode('bar')}
            size="sm"
            variant={viewMode === 'bar' ? 'default' : 'outline'}
            disabled={numberColumns.length === 0}
          >
            <BarChart3 className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => updateViewMode('pie')}
            size="sm"
            variant={viewMode === 'pie' ? 'default' : 'outline'}
            disabled={numberColumns.length === 0}
          >
            <PieChartIcon className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => updateViewMode('line')}
            size="sm"
            variant={viewMode === 'line' ? 'default' : 'outline'}
            disabled={numberColumns.length === 0}
          >
            <TrendingUp className="w-4 h-4" />
          </Button>
          {((viewMode !== 'table' && chartData.length > 0) || (viewMode === 'table' && rows.length > 0)) && (
            <>
              {viewMode !== 'table' && (
                <Button
                  onClick={exportChart}
                  size="sm"
                  variant="ghost"
                  title="Exporter le graphique"
                >
                  <Download className="w-4 h-4" />
                </Button>
              )}
              {insights ? (
                <Button
                  onClick={() => {
                    setIsAIPanelOpen(!isAIPanelOpen);
                    savePreference('isAIPanelOpen', !isAIPanelOpen);
                  }}
                  size="sm"
                  variant={isAIPanelOpen ? "default" : "outline"}
                  title={isAIPanelOpen ? "Masquer les insights IA" : "Afficher les insights IA"}
                  className="group relative overflow-hidden bg-gradient-to-r from-[hsl(var(--ai-primary)/0.1)] to-[hsl(var(--ai-accent)/0.1)] border-[hsl(var(--ai-primary)/0.3)] hover:border-[hsl(var(--ai-primary)/0.5)] transition-all duration-300 hover:shadow-[0_0_20px_hsl(var(--ai-glow)/0.3)]"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ai-primary)/0.2)] to-[hsl(var(--ai-accent)/0.2)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-center gap-2">
                    <Sparkles className="w-4 h-4 group-hover:scale-110 transition-transform duration-300 text-[hsl(var(--ai-primary))]" />
                    <span className="text-xs">{isAIPanelOpen ? 'Masquer' : 'Afficher'}</span>
                    {isCached && analysisAge !== null && analysisAge > 7 && (
                      <span className="ml-1 text-xs text-muted-foreground">({analysisAge}j)</span>
                    )}
                  </div>
                </Button>
              ) : quota.remaining > 0 ? (
                <Button
                  onClick={() => {
                    handleAnalyzeChart();
                    if (!isAIPanelOpen) {
                      setIsAIPanelOpen(true);
                      savePreference('isAIPanelOpen', true);
                    }
                  }}
                  size="sm"
                  variant="outline"
                  disabled={isAnalyzing}
                  className="group relative overflow-hidden bg-gradient-to-r from-[hsl(var(--ai-primary)/0.1)] to-[hsl(var(--ai-accent)/0.1)] border-[hsl(var(--ai-primary)/0.3)] hover:border-[hsl(var(--ai-primary)/0.5)] transition-all duration-300 hover:shadow-[0_0_20px_hsl(var(--ai-glow)/0.3)] disabled:opacity-50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ai-primary)/0.2)] to-[hsl(var(--ai-accent)/0.2)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-center gap-2">
                    <Sparkles className="w-4 h-4 group-hover:scale-110 transition-transform duration-300 text-[hsl(var(--ai-primary))]" />
                    <span className="text-xs">{isAnalyzing ? 'Analyse...' : `Analyser (${quota.remaining}/${quota.limit})`}</span>
                  </div>
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled
                  className="opacity-50 cursor-not-allowed"
                  title={`Quota quotidien atteint (${quota.used}/${quota.limit})`}
                >
                  <Sparkles className="w-4 h-4 mr-2 text-muted-foreground" />
                  <span className="text-xs">Quota atteint</span>
                </Button>
               )}
            </>
          )}
        </div>
      </div>

      {/* Contenu avec panneau AI en grille */}
      <div className={`grid gap-4 ${insights && isAIPanelOpen ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Contenu principal */}
        <div className={`space-y-4 ${insights && isAIPanelOpen ? 'lg:col-span-1' : 'col-span-1'}`}>
          {viewMode === 'table' ? (
            <div className="space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <Table aria-label={`Tableau de données ${name}`} role="table">
                  <caption className="sr-only">
                    {`${name} - Tableau contenant ${rows.length} lignes avec ${columns.length} colonnes : ${columns.map(c => c.name).join(', ')}. Page ${currentPage} sur ${totalPages}.`}
                  </caption>
                  <TableHeader>
                    <TableRow>
                      {columns.map((column) => (
                        <TableHead key={column.id} scope="col">{column.name}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRows.map((row, index) => (
                      <TableRow key={index}>
                        {columns.map((column, colIndex) => (
                          <TableCell 
                            key={column.id}
                            {...(colIndex === 0 ? { scope: 'row' } : {})}
                          >
                            {row[column.id] || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
          
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, rows.length)} sur {rows.length} lignes
              </p>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNum)}
                          isActive={currentPage === pageNum}
                          className="cursor-pointer"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                {renderChart()}
                {selectedSlice && (
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Détails sélectionnés :</h4>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ 
                          backgroundColor: selectedSliceIndex !== null ? COLORS[selectedSliceIndex % COLORS.length] : '#ccc'
                        }}
                      />
                      <span className="font-medium">
                        {columns.find(col => col.id === selectedXColumn)?.name}: {selectedSlice}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {columns.find(col => col.id === selectedYColumn)?.name}: {
                        chartData.find(item => item.name === selectedSlice)?.value || 0
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Configuration des colonnes pour les graphiques */}
          {viewMode !== 'table' && numberColumns.length > 0 && (
            <div className="space-y-3">
              <div className="flex gap-2 items-center text-sm">
                <span>Colonnes:</span>
                <Select value={selectedXColumn} onValueChange={updateXColumn}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="X (catégories)" />
                  </SelectTrigger>
                  <SelectContent>
                    {textColumns.map(col => (
                      <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedYColumn} onValueChange={updateYColumn}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Y (valeurs)" />
                  </SelectTrigger>
                  <SelectContent>
                    {numberColumns.map(col => (
                      <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 items-center text-sm">
                <span>Affichage:</span>
                <Select value={maxItems.toString()} onValueChange={(value) => updateMaxItems(parseInt(value))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 éléments</SelectItem>
                    <SelectItem value="10">10 éléments</SelectItem>
                    <SelectItem value="15">15 éléments</SelectItem>
                    <SelectItem value="20">20 éléments</SelectItem>
                    <SelectItem value="50">50 éléments</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={sortOrder} onValueChange={(value: 'asc' | 'desc') => updateSortOrder(value)}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Décroissant</SelectItem>
                    <SelectItem value="asc">Croissant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Panneau AI en grille */}
        {insights && isAIPanelOpen && (
          <div className="lg:col-span-1 h-[400px]">
            <AIInsights 
              insights={insights} 
              onClose={clearInsights}
              onRegenerate={handleAnalyzeChart}
            />
          </div>
        )}
      </div>
    </div>
  );
};